import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  FiShoppingCart, FiStar, FiMinus, FiPlus, FiTruck,
  FiShield, FiRefreshCw, FiChevronRight, FiHeart, FiBarChart2,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useCart } from '../../context/CartContext';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ProductCard from '../../components/product/ProductCard';
import ReviewSection from '../../components/product/ReviewSection';
import QASection from '../../components/product/QASection';
import ImageGallery from '../../components/product/ImageGallery';
import RecentlyViewed from '../../components/product/RecentlyViewed';
import ProductRecommendations from '../../components/product/ProductRecommendations';
import SocialShare from '../../components/product/SocialShare';
import useRecentlyViewed from '../../hooks/useRecentlyViewed';
import productService from '../../services/product.service';
import ContactSellerButton from '../../components/chat/ContactSellerButton';
import VariantSelector from '../../components/product/VariantSelector';

const ProductDetailPage = () => {
  const { slug } = useParams();
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  const { recentlyViewed, addProduct: trackView } = useRecentlyViewed();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [addingToCart, setAddingToCart] = useState(false);
  const [activeTab, setActiveTab] = useState('description');
  const { lang } = useLanguage();

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const { data } = await productService.getBySlug(slug);
        setProduct(data.data);
        trackView(data.data);
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error('Product not found');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
    window.scrollTo(0, 0);
  }, [slug, lang]);

  const handleAddToCart = async () => {
    setAddingToCart(true);
    try {
      await addToCart(product._id, quantity, selectedVariant?._id);
      toast.success(`${product.name} added to cart!`);
    } catch (error) {
      toast.error('Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleQuantityChange = (delta) => {
    const newQty = quantity + delta;
    if (newQty < 1) return;
    if (product.stock?.trackInventory && newQty > product.stock.quantity) {
      toast.warning('Maximum available stock reached');
      return;
    }
    setQuantity(newQty);
  };

  const discountPercent = product?.compareAtPrice && product.compareAtPrice > product.price
    ? Math.round(((product.compareAtPrice - product.price) / product.compareAtPrice) * 100)
    : 0;

  const inStock = !product?.stock?.trackInventory || product.stock.quantity > 0;
  const lowStock = product?.stock?.trackInventory &&
    product.stock.quantity > 0 &&
    product.stock.quantity <= product.stock.lowStockThreshold;

  if (loading) return <LoadingSpinner />;

  if (!product) {
    return (
      <div className="section-padding text-center">
        <h3>Product not found</h3>
        <Link to="/products" className="btn btn-ebw-primary mt-3">{t('browseProducts')}</Link>
      </div>
    );
  }

  return (
    <div className="section-padding">
      <div className="container">
        {/* Breadcrumb */}
        <nav className="mb-4">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><Link to="/">Home</Link></li>
            <li className="breadcrumb-item"><Link to="/products">Products</Link></li>
            {product.category && (
              <li className="breadcrumb-item">
                <Link to={`/products?category=${product.category}`}>{product.category}</Link>
              </li>
            )}
            <li className="breadcrumb-item active">{product.name}</li>
          </ol>
        </nav>

        <div className="row g-4 g-lg-5">
          {/* Image Gallery */}
          <div className="col-lg-6">
            <div className="product-detail-gallery position-relative">
              <ImageGallery images={product.images} />

              {discountPercent > 0 && (
                <span className="position-absolute top-0 start-0 m-3 badge bg-danger fs-6" style={{ zIndex: 3 }}>
                  -{discountPercent}%
                </span>
              )}
            </div>
          </div>

          {/* Product Info */}
          <div className="col-lg-6">
            <div className="product-detail-info">
              <p className="text-uppercase text-muted small fw-semibold mb-1 tracking-wide">
                {product.category}
                {product.subcategory && ` / ${product.subcategory}`}
              </p>

              <h1 className="h2 fw-bold mb-3">{product.name}</h1>

              {/* Rating */}
              {product.ratings?.count > 0 && (
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div className="d-flex align-items-center text-warning">
                    {Array.from({ length: 5 }, (_, i) => (
                      <FiStar
                        key={i}
                        size={16}
                        fill={i < Math.round(product.ratings.average) ? '#EAB308' : 'none'}
                        stroke="#EAB308"
                      />
                    ))}
                  </div>
                  <span className="text-muted small">
                    {product.ratings.average.toFixed(1)} ({product.ratings.count} reviews)
                  </span>
                </div>
              )}

              {/* Price */}
              <div className="mb-4">
                <span className="fs-2 fw-bold" style={{ color: '#B91C1C' }}>
                  ${(selectedVariant?.price || product.price).toFixed(2)}
                </span>
                {(selectedVariant?.compareAtPrice || product.compareAtPrice) > (selectedVariant?.price || product.price) && (
                  <span className="fs-5 text-muted text-decoration-line-through ms-2">
                    ${(selectedVariant?.compareAtPrice || product.compareAtPrice).toFixed(2)}
                  </span>
                )}
                {selectedVariant && <span className="ms-2 badge bg-light text-muted">{selectedVariant.name}</span>}
              </div>

              {/* Variant Selector */}
              {product.hasVariants && product.options?.length > 0 && (
                <VariantSelector
                  options={product.options}
                  variants={product.variants}
                  basePrice={product.price}
                  onSelect={setSelectedVariant}
                />
              )}

              {/* Short description */}
              {product.shortDescription && (
                <p className="text-muted mb-4">{product.shortDescription}</p>
              )}

              {/* Stock Status */}
              <div className="mb-4">
                {inStock ? (
                  <span className={`badge ${lowStock ? 'bg-warning' : 'bg-success'} fs-6`}>
                    {lowStock ? `Only ${product.stock.quantity} left` : 'In Stock'}
                  </span>
                ) : (
                  <span className="badge bg-danger fs-6">{t('outOfStock')}</span>
                )}
                {product.sku && (
                  <span className="text-muted small ms-3">SKU: {product.sku}</span>
                )}
              </div>

              {/* Quantity + Add to Cart */}
              {inStock && (
                <div className="d-flex align-items-center gap-3 mb-4">
                  <div className="quantity-control d-flex align-items-center border rounded">
                    <button
                      className="btn btn-sm px-3 py-2 border-0"
                      onClick={() => handleQuantityChange(-1)}
                      disabled={quantity <= 1}
                    >
                      <FiMinus size={16} />
                    </button>
                    <span className="px-3 fw-semibold fs-5">{quantity}</span>
                    <button
                      className="btn btn-sm px-3 py-2 border-0"
                      onClick={() => handleQuantityChange(1)}
                    >
                      <FiPlus size={16} />
                    </button>
                  </div>

                  <button
                    className="btn btn-ebw-primary btn-lg flex-grow-1"
                    onClick={handleAddToCart}
                    disabled={addingToCart}
                  >
                    <FiShoppingCart className="me-2" />
                    {addingToCart ? 'Adding...' : 'Add to Cart'}
                  </button>

                  <button className="btn btn-outline-secondary btn-lg" title="Add to Wishlist">
                    <FiHeart size={20} />
                  </button>
                  {product.merchantId && (
                    <ContactSellerButton
                      merchantId={product.merchantId}
                      productId={product._id}
                      variant="icon"
                    />
                  )}
                  <Link
                    to={`/compare?ids=${product._id}`}
                    className="btn btn-outline-secondary btn-lg"
                    title="Compare"
                    onClick={(e) => {
                      // If there's already items in compare from localStorage
                      const existing = localStorage.getItem('ebuywiz_compare') || '';
                      const ids = existing.split(',').filter(Boolean);
                      if (!ids.includes(product._id)) ids.push(product._id);
                      localStorage.setItem('ebuywiz_compare', ids.join(','));
                    }}
                  >
                    <FiBarChart2 size={20} />
                  </Link>
                </div>
              )}

              {/* Trust Badges */}
              <div className="row g-3 mt-2">
                <div className="col-4">
                  <div className="text-center p-2 rounded bg-light">
                    <FiTruck size={20} className="text-primary mb-1 d-block mx-auto" />
                    <small className="text-muted">
                      {product.shipping?.freeShipping ? 'Free Shipping' : 'Fast Delivery'}
                    </small>
                  </div>
                </div>
                <div className="col-4">
                  <div className="text-center p-2 rounded bg-light">
                    <FiShield size={20} className="text-primary mb-1 d-block mx-auto" />
                    <small className="text-muted">Secure Payment</small>
                  </div>
                </div>
                <div className="col-4">
                  <div className="text-center p-2 rounded bg-light">
                    <FiRefreshCw size={20} className="text-primary mb-1 d-block mx-auto" />
                    <small className="text-muted">Easy Returns</small>
                  </div>
                </div>
              </div>

              {/* Tags */}
              {product.tags?.length > 0 && (
                <div className="mt-4">
                  <span className="text-muted small me-2">Tags:</span>
                  {product.tags.map((tag, i) => (
                    <Link
                      key={i}
                      to={`/products?search=${tag}`}
                      className="badge bg-light text-dark me-1 mb-1 text-decoration-none"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>
              )}

              {/* Social Share */}
              <div className="mt-4 pt-3 border-top">
                <SocialShare
                  title={product.name}
                  url={window.location.href}
                  image={product.images?.[0]?.url}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs: Description / Specifications / Reviews */}
        <div className="mt-5">
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'description' ? 'active' : ''}`}
                onClick={() => setActiveTab('description')}
              >
                Description
              </button>
            </li>
            {product.attributes && Object.keys(product.attributes).length > 0 && (
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'specifications' ? 'active' : ''}`}
                  onClick={() => setActiveTab('specifications')}
                >
                  Specifications
                </button>
              </li>
            )}
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'reviews' ? 'active' : ''}`}
                onClick={() => setActiveTab('reviews')}
              >
                Reviews ({product.ratings?.count || 0})
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'qa' ? 'active' : ''}`}
                onClick={() => setActiveTab('qa')}
              >
                Q&A
              </button>
            </li>
          </ul>

          <div className="dashboard-card mt-0 rounded-top-0 border-top-0">
            {activeTab === 'description' && (
              <div className="product-description">
                <p style={{ whiteSpace: 'pre-line' }}>{product.description}</p>
              </div>
            )}

            {activeTab === 'specifications' && product.attributes && (
              <div className="product-specifications">
                <table className="table table-striped mb-0">
                  <tbody>
                    {Object.entries(
                      product.attributes instanceof Map
                        ? Object.fromEntries(product.attributes)
                        : product.attributes
                    ).map(([key, value]) => (
                      <tr key={key}>
                        <td className="fw-semibold text-capitalize" style={{ width: '30%' }}>
                          {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ')}
                        </td>
                        <td>{String(value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Shipping info */}
                {product.shipping && (
                  <div className="mt-3 pt-3 border-top">
                    <h6>Shipping Details</h6>
                    <div className="row">
                      {product.shipping.weight && (
                        <div className="col-md-4">
                          <span className="text-muted">Weight:</span> {product.shipping.weight}g
                        </div>
                      )}
                      {product.shipping.dimensions && (
                        <div className="col-md-8">
                          <span className="text-muted">Dimensions:</span>{' '}
                          {product.shipping.dimensions.length} x{' '}
                          {product.shipping.dimensions.width} x{' '}
                          {product.shipping.dimensions.height} cm
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'reviews' && (
              <ReviewSection
                productId={product._id}
                isAuthenticated={isAuthenticated}
              />
            )}

            {activeTab === 'qa' && (
              <QASection
                productId={product._id}
                isAuthenticated={isAuthenticated}
              />
            )}
          </div>
        </div>

        {/* Recommendations */}
        <div className="mt-5">
          <ProductRecommendations type="also-bought" productId={product._id} />
          <ProductRecommendations type="similar" productId={product._id} />
        </div>

        {/* Recently Viewed */}
        <RecentlyViewed items={recentlyViewed} currentProductId={product._id} />
      </div>
    </div>
  );
};

export default ProductDetailPage;
